import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { CHAPTERS } from "../lib/chapters";
import { PathologiesForChapter } from "../components/ResearchCrossLinks";
import { toast } from "sonner";

const ChapterPage = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const [readingMode, setReadingMode] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // slug format: "chapitre-X-slug" — extract the chapter id
  const chapter = useMemo(() => {
    const match = slug?.match(/^chapitre-(\d+)-(.+)$/);
    if (!match) return null;
    const id = parseInt(match[1], 10);
    return CHAPTERS.find((c) => c.id === id) || null;
  }, [slug]);

  if (!chapter) {
    return (
      <main className="bg-white pt-36 pb-20 text-center">
        <div className="container-page">
          <h1 className="font-display font-black text-4xl text-navy">
            {lang === "fr" ? "Chapitre introuvable" : "Chapter not found"}
          </h1>
          <Link to="/recherche" className="mt-6 inline-block link-underline">
            {lang === "fr" ? "Retour au centre bibliographique" : "Back to bibliography"}
          </Link>
        </div>
      </main>
    );
  }

  // Cross-chapter nav
  const idx = CHAPTERS.findIndex((c) => c.id === chapter.id);
  const prev = idx > 0 ? CHAPTERS[idx - 1] : null;
  const next = idx < CHAPTERS.length - 1 ? CHAPTERS[idx + 1] : null;

  // BibTeX export of the references
  const exportBibTeX = () => {
    const entries = (chapter.references || []).map((r, i) => {
      const key = `aswad${chapter.id}_${i + 1}`;
      return `@article{${key},\n  note = {${r.text.replace(/}/g, "\\}").replace(/{/g, "\\{")}}${r.pubmed ? `,\n  url = {https://pubmed.ncbi.nlm.nih.gov/${r.pubmed}/}` : ""}\n}`;
    }).join("\n\n");
    const blob = new Blob([`% Centre bibliographique IMOTEpS — Dr Richard Aswad\n% Chapitre ${chapter.id} : ${chapter.title}\n\n${entries}\n`], { type: "application/x-bibtex" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `imoteps-ch${chapter.id}-${chapter.slug}.bib`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(lang === "fr" ? "Fichier BibTeX téléchargé" : "BibTeX file downloaded");
  };

  // RIS export
  const exportRIS = () => {
    const entries = (chapter.references || []).map((r) => {
      let s = "TY  - JOUR\n";
      s += `T1  - ${r.text}\n`;
      if (r.pubmed) s += `UR  - https://pubmed.ncbi.nlm.nih.gov/${r.pubmed}/\n`;
      s += "ER  - \n";
      return s;
    }).join("\n");
    const blob = new Blob([entries], { type: "application/x-research-info-systems" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `imoteps-ch${chapter.id}-${chapter.slug}.ris`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(lang === "fr" ? "Fichier RIS téléchargé" : "RIS file downloaded");
  };

  const printPage = () => {
    window.print();
  };

  // JSON-LD Scholarly article
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalScholarlyArticle",
    "headline": chapter.title,
    "description": chapter.summary,
    "author": { "@type": "Person", "name": "Dr Richard Aswad" },
    "publisher": { "@type": "Organization", "name": "IMOTEpS" },
    "about": { "@type": "MedicalCondition", "name": chapter.region },
    "inLanguage": "fr-FR",
    "datePublished": "2026-01-01",
  };

  return (
    <main data-testid="chapter-page" className="bg-white pt-24 md:pt-32 pb-20 chapter-print">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Color top band */}
      <div className="h-1.5 w-full" style={{ backgroundColor: chapter.color }} />

      <article className={`container-page ${readingMode ? "max-w-2xl" : "max-w-4xl"}`}>
        {/* Top toolbar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Link to="/recherche" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-[#FF5A1F]">
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
            {lang === "fr" ? "Centre bibliographique" : "Bibliography center"}
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setReadingMode((v) => !v)}
              data-testid="reading-mode-toggle"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-navy hover:border-[#FF5A1F]/40 transition"
              title={lang === "fr" ? "Mode lecture" : "Reading mode"}
            >
              <i className={`fa-solid ${readingMode ? "fa-book-open" : "fa-book"}`} aria-hidden="true"></i>
              {lang === "fr" ? (readingMode ? "Mode large" : "Mode lecture") : (readingMode ? "Wide" : "Reading")}
            </button>
            <button type="button" onClick={exportBibTeX} data-testid="export-bibtex" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-navy hover:border-[#FF5A1F]/40 transition">
              <i className="fa-solid fa-quote-right" aria-hidden="true"></i>
              BibTeX
            </button>
            <button type="button" onClick={exportRIS} data-testid="export-ris" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-navy hover:border-[#FF5A1F]/40 transition">
              <i className="fa-solid fa-file-export" aria-hidden="true"></i>
              RIS
            </button>
            <button type="button" onClick={printPage} data-testid="print-button" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-navy hover:border-[#FF5A1F]/40 transition">
              <i className="fa-solid fa-print" aria-hidden="true"></i>
              {lang === "fr" ? "Imprimer" : "Print"}
            </button>
          </div>
        </div>

        {/* Header */}
        <header className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="rounded-full px-3 py-1 text-xs font-display font-extrabold uppercase tracking-[0.22em] text-white"
              style={{ backgroundColor: chapter.color }}
            >
              Chapitre {chapter.id}
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: chapter.color }}>
              {chapter.region}
            </span>
            <span className="text-xs text-muted-foreground">
              · {chapter.readingMin} {lang === "fr" ? "min de lecture" : "min read"}
            </span>
            <span className="text-xs text-muted-foreground">
              · {chapter.references?.length || 0} {lang === "fr" ? "références" : "references"}
            </span>
          </div>
          <h1
            className="mt-5 font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]"
            style={{ borderBottom: `4px solid ${chapter.color}`, paddingBottom: "0.5rem", display: "inline-block" }}
          >
            {chapter.title}
          </h1>
          <p className="mt-4 text-lg text-foreground/80 leading-snug">{chapter.subtitle}</p>
          <p className="mt-5 text-base text-foreground/75 leading-relaxed italic">
            <strong>{lang === "fr" ? "Résumé." : "Abstract."}</strong> {chapter.summary}
          </p>
          {chapter.audience && (
            <p className="mt-3 text-sm font-semibold" style={{ color: chapter.color }}>
              <i className="fa-solid fa-user-group mr-2" aria-hidden="true"></i>
              {chapter.audience}
            </p>
          )}
        </header>

        {/* Body sections */}
        <div className={`mt-10 ${readingMode ? "leading-[1.85] text-[17px]" : "leading-relaxed"}`}>
          {chapter.sections.map((s, i) => (
            <section key={i} data-testid={`chapter-section-${i}`} className="mb-9">
              <h2
                className="font-display text-2xl font-bold text-navy"
                style={{ borderLeft: `4px solid ${chapter.color}`, paddingLeft: "0.75rem" }}
              >
                {s.heading}
              </h2>
              {s.body && <p className="mt-4 text-foreground/85 whitespace-pre-line">{s.body}</p>}
              {s.list && (
                <ul className="mt-4 space-y-2 pl-1">
                  {s.list.map((li, j) => (
                    <li key={j} className="flex gap-3 text-foreground/85">
                      <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: chapter.color }}></span>
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* References */}
        {chapter.references && chapter.references.length > 0 && (
          <section className="mt-14 pt-8 border-t border-border" data-testid="chapter-references">
            <h2 className="font-display text-2xl font-bold text-navy">
              {lang === "fr" ? "Références" : "References"}
            </h2>
            <ol className="mt-5 space-y-3">
              {chapter.references.map((r) => (
                <li key={r.id} className="text-sm text-foreground/80 leading-relaxed flex gap-3">
                  <span className="font-display font-bold w-7 shrink-0 text-right" style={{ color: chapter.color }}>{r.id}.</span>
                  <span>
                    {r.text}
                    {r.pubmed && (
                      <a
                        href={`https://pubmed.ncbi.nlm.nih.gov/${r.pubmed}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                        style={{ color: chapter.color }}
                      >
                        PubMed
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Cross-link to clinical pathology pages */}
        <PathologiesForChapter chapterId={chapter.id} lang={lang} />

        {/* Inter-chapter nav */}
        <nav className="mt-14 grid sm:grid-cols-2 gap-4 print:hidden">
          {prev ? (
            <Link
              to={`/recherche/chapitre-${prev.id}-${prev.slug}`}
              className="group rounded-xl border border-border bg-white p-5 hover:shadow-md transition"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "← Chapitre précédent" : "← Previous chapter"}
              </p>
              <p className="mt-2 font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                {prev.title}
              </p>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to={`/recherche/chapitre-${next.id}-${next.slug}`}
              className="group rounded-xl border border-border bg-white p-5 hover:shadow-md transition text-right"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "Chapitre suivant →" : "Next chapter →"}
              </p>
              <p className="mt-2 font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                {next.title}
              </p>
            </Link>
          ) : <div />}
        </nav>

        {/* Footer attribution */}
        <footer className="mt-14 pt-6 border-t border-border text-xs text-muted-foreground print:mt-8">
          <p>
            <strong>Dr Richard Aswad</strong> — Chirurgien orthopédique, IMOTEpS, 118 rue Jean Mermoz, 13008 Marseille.
          </p>
          <p className="mt-1 italic">
            {lang === "fr"
              ? "Document scientifique. Ne se substitue pas à une consultation médicale individualisée."
              : "Scientific document. Does not replace an individualized medical consultation."}
          </p>
        </footer>
      </article>
    </main>
  );
};

export default ChapterPage;
